/**
 * M1-12a — generating a setup code (§6.3, §7.4).
 *
 * §7.4 calls this the expected way a till is set up, and the reason is a
 * scene: the owner is not at the shop, or is standing next to a cashier, and
 * the alternative is typing their portal password on the machine or handing it
 * to a technician. A code delegates one installation and nothing else.
 *
 * Three things the screen has to get right, all of them about the code being
 * read out loud to somebody else:
 *
 * - **It is shown once.** Nothing stores it, so nothing can show it again. The
 *   card says so before generating rather than after, or an owner closes the
 *   page and is surprised.
 * - **The countdown is live.** "Expires in 12 minutes" written once is wrong a
 *   minute later, and an owner reading a stale number will hand over a code
 *   that dies on the way.
 * - **It is easy to read aloud.** Big, spaced, in a monospace face where 8 and
 *   B are different shapes — because the person typing it is not looking at it.
 */

import { useCallback, useEffect, useState } from 'react'

import { api, type EnrolmentCode, type LiveEnrolmentCode } from '../api.js'
import { Alert, Card } from '../components/ui.js'
import { messageFor } from './Register.js'

/** `m:ss`, so a glance says whether there is time to finish reading it out. */
function countdown(seconds: number): string {
  const safe = Math.max(0, seconds)
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`
}

export function SetupCode({ canEnrol }: { canEnrol: boolean }) {
  const [issued, setIssued] = useState<EnrolmentCode | null>(null)
  const [live, setLive] = useState<LiveEnrolmentCode | null>(null)
  const [remaining, setRemaining] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  const refresh = useCallback(async () => {
    const res = await api.liveEnrolmentCode()
    setLive(res.code)
    if (res.code) setRemaining(res.code.secondsRemaining)
  }, [])

  useEffect(() => {
    // Guarded rather than relying on the early return below: hooks run before
    // it, so without this the card asks the server about a code every time the
    // Devices page loads with the slot already taken — a request on a shop's
    // mobile connection for something nobody is going to see.
    if (!canEnrol) return
    void refresh().catch(() => undefined)
  }, [refresh, canEnrol])

  /**
   * Tick locally rather than polling.
   *
   * A request a second to watch a number go down is rude to a shop on a mobile
   * connection, and the server has already told us when it expires.
   */
  useEffect(() => {
    if (remaining <= 0) return
    const timer = window.setInterval(() => {
      setRemaining((value) => {
        const next = value - 1
        if (next <= 0) {
          // The code is dead; stop showing it rather than leaving it on screen
          // for an owner to read out to somebody.
          setIssued(null)
          setLive(null)
        }
        return next
      })
    }, 1000)
    return () => window.clearInterval(timer)
  }, [remaining])

  async function generate() {
    setBusy(true)
    setError(null)
    setCopied(false)
    try {
      const code = await api.createEnrolmentCode()
      setIssued(code)
      setRemaining(Math.max(0, Math.round((Date.parse(code.expiresAt) - Date.now()) / 1000)))
      await refresh()
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  async function copy(code: string) {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Refused on an insecure origin, among other ordinary cases. The code is
      // on screen and selectable, so this is a convenience failing rather than
      // the feature failing, and a banner would be out of proportion.
    }
  }

  if (!canEnrol) return null

  return (
    <Card
      title="Set up a new till"
      note="Generate a code and give it to whoever is setting up the PC. They type it at first run — they never need your password, and you do not need to be there."
    >
      {error ? <Alert>{error}</Alert> : null}

      {issued && remaining > 0 ? (
        <>
          <p className="setup-code" aria-label="Setup code">
            {issued.code}
          </p>
          <p className="setup-code__timer">
            Expires in <strong>{countdown(remaining)}</strong>
          </p>
          <div className="actions">
            <button type="button" className="small" onClick={() => void copy(issued.code)}>
              {copied ? 'Copied' : 'Copy code'}
            </button>
            <button type="button" className="small" onClick={() => void generate()} disabled={busy}>
              Generate a new one
            </button>
          </div>
          <Alert tone="warn">
            Write it down or read it out now — this is the only time it is shown. Generating
            another replaces this one.
          </Alert>
        </>
      ) : (
        <>
          {live && remaining > 0 ? (
            // A reload landed here. We cannot show the code again, and saying
            // so is better than implying they need a new one when the one they
            // already wrote down still works.
            <p>
              A code is still live for another <strong>{countdown(remaining)}</strong>. It cannot
              be shown again — generate a new one if you no longer have it.
            </p>
          ) : (
            <p className="empty">No setup code at the moment.</p>
          )}
          <div className="actions">
            <button type="button" className="primary" onClick={() => void generate()} disabled={busy}>
              {busy ? 'Generating…' : live && remaining > 0 ? 'Generate a new code' : 'Generate a setup code'}
            </button>
          </div>
        </>
      )}

      <p className="card__note" style={{ marginBottom: 0 }}>
        A code works once and lasts a few minutes. It sets up one machine and gives no other
        access to your account.
      </p>
    </Card>
  )
}
